import { Button, Col, Row, Space, Table, Tag, Typography } from "antd";
import { examPeriodActivityTable } from "@configurations";
import {
  ExamPeriod,
  ExamPeriodMonitor,
  ExamPeriodMonitorState,
  ExamPeriodState,
} from "@types";
import {
  useEditExamInstanceMonitor,
  useCompleteExamPeriod,
  useSignalRConnection
} from "@hooks";
import dayjs from "dayjs";
import { setTimeWithSeconds } from "../../helpers";
import { ExamTermTime } from "../ExamTermTime";
import { useTranslation } from "react-i18next";
import { getTextByExamPeriodStatus } from "./helpers";
import { AnswerAuditPanel } from "./AnswerAuditPanel";
import { canViewAnswerAudit } from "./answerAuditAccess";
import { applyMonitorStateChange } from "./monitorStateSync";
import { getKeycloakRealm } from "@/components/helpers";
import { useExamStore } from "@store";
import { Flag } from "lucide-react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  exam?: ExamPeriod;
  monitorData?: ExamPeriodMonitor[];
  isLoading: boolean;
}

export const ExamPeriodMonitorTable = ({
  exam,
  monitorData,
  isLoading,
}: Props) => {
  const editExamInstance = useEditExamInstanceMonitor();
  const { t } = useTranslation();
  const completeExamPeriod = useCompleteExamPeriod();
  const [buttonClicked, setButtonClicked] = useState<number>(0);
  const { newMessage, events, joinExamTermMonitoring, leaveExamTermMonitoring } = useSignalRConnection();
  const queryClient = useQueryClient();

  // Audit zapise predaja odgovora smiju vidjeti ISKLJUČIVO Admin i Assessor role.
  const currentUser = useExamStore((state) => state.currentUser);
  const canSeeAnswerAudit = canViewAnswerAudit(currentUser);

  const getProctoringEventLabel = (eventType: string) => {
    switch (eventType) {
      case "ExamEntered":
        return t("pages.activity.examEntered");
      case "VisibilityHidden":
        return t("pages.activity.visibilityHidden");
      case "VisibilityVisible":
        return t("pages.activity.visibilityVisible");
      case "BrowserExit":
        return t("pages.activity.browserExit");
      default:
        return eventType;
    }
  };

  const getMonitorStateLabel = (state: ExamPeriodMonitorState) => {
    switch (state) {
      case ExamPeriodMonitorState.WaitingForParticipant:
        return t("pages.examPeriods.waitingForParticipant");
      case ExamPeriodMonitorState.ParticipantConfirmed:
        return t("pages.examPeriods.participantConfirmed");
      case ExamPeriodMonitorState.NoShow:
        return t("pages.examPeriods.notShowed");
      case ExamPeriodMonitorState.InProgress:
        return t("pages.examPeriods.inProgress");
      case ExamPeriodMonitorState.Paused:
        return t("pages.examPeriods.paused");
      case ExamPeriodMonitorState.Excluded:
        return t("pages.examPeriods.disqualified");
      case ExamPeriodMonitorState.Completed:
        return t("pages.examPeriods.completed");
      default:
        return String(state);
    }
  };


  const handleClickAll = (state: number) => {
    setButtonClicked(state);
    let arr;
    if (state === 2) {
      arr = monitorData
        ?.map((item) =>
          item.state === ExamPeriodMonitorState.WaitingForParticipant
            ? item.id
            : null
        )
        .filter((item) => item !== null) as string[];
    } else {
      arr = monitorData
        ?.map((item) =>
          item.state !== ExamPeriodMonitorState.ParticipantConfirmed &&
          item.state !== ExamPeriodMonitorState.Completed &&
          item.state !== ExamPeriodMonitorState.Excluded
            ? item.id
            : null
        )
        .filter((item) => item !== null) as string[];
    }

    const val = {
      ids: arr,
      newState: state,
      examId: exam?.id,
    };

    editExamInstance.mutate(val);
  };

  const checkIfRegisterAll = () => {
    if (monitorData) {
      for (let i = 0; i < monitorData.length; i++) {
        if (monitorData[i].state === 1) {
          return false;
        }
      }
    }
    return true;
  };

  useEffect(() => {
    if (!exam?.id) {
      return;
    }

    joinExamTermMonitoring(exam.id);
    const rejoinMonitoringGroup = () => joinExamTermMonitoring(exam.id!);
    window.addEventListener("signalr-connected", rejoinMonitoringGroup);

    return () => {
      window.removeEventListener("signalr-connected", rejoinMonitoringGroup);
      leaveExamTermMonitoring(exam.id);
    };
  }, [exam?.id, joinExamTermMonitoring, leaveExamTermMonitoring]);

  useEffect(() => {
    console.log("Setting up SignalR listener for exam:", exam?.id);
    
    const handleMessage = (type: string, message: string) => {
      
      if (type === "status") {
        // Parse message to extract action and ID
        // Expected format: "action-id" (e.g., "start-bff2c25b-4d9b-4037-ac82-991ec966dae1")
        const parts = message.split("-");
        if (parts.length < 2) {
          console.warn("Invalid message format:", message);
          return;
        }

        const action = parts[0]; // "start", "finish", "continue", etc.
        const examInstanceId = message.substring(action.length + 1); // Everything after "action-"
        
        console.log("Parsed message:", { action, examInstanceId, fullMessage: message });
        
        // Get current monitor data from query cache
        const queryKey = ["examPeriods", exam?.id, "monitor"];
        const currentMonitorData = queryClient.getQueryData<ExamPeriodMonitor[]>(queryKey);
        
        console.log("Current monitor data from cache:", {
          hasData: !!currentMonitorData,
          dataLength: currentMonitorData?.length,
          queryKey
        });

        if (!currentMonitorData || currentMonitorData.length === 0) {
          console.warn("No monitor data in cache, invalidating to refetch...");
          queryClient.invalidateQueries({ queryKey });
          return;
        }

        // Find the exam period activity by ID
        const examPeriodActivity = currentMonitorData.find(
          (item) => item.id === examInstanceId
        );

        if (!examPeriodActivity) {
          console.warn("Exam period activity not found for ID:", examInstanceId);
          console.log("Available IDs:", currentMonitorData.map(item => item.id));
          // Still invalidate to get fresh data
          queryClient.invalidateQueries({ queryKey });
          return;
        }

        console.log("Found exam period activity:", {
          id: examPeriodActivity.id,
          currentState: examPeriodActivity.state,
          action
        });

        // Determine new state based on action
        let newState: ExamPeriodMonitorState | null = null;
        if (action === "start") {
          newState = ExamPeriodMonitorState.InProgress;
          console.log("Updating state to InProgress for:", examInstanceId);
        } else if (action === "finish") {
          newState = ExamPeriodMonitorState.Completed;
          console.log("Updating state to Completed for:", examInstanceId);
        } else if (action === "continue") {
          newState = ExamPeriodMonitorState.InProgress;
          console.log("Updating state to InProgress (continue) for:", examInstanceId);
        } else if (action === "pause") {
          newState = ExamPeriodMonitorState.Paused;
          console.log("Updating state to Paused for:", examInstanceId);
        } else {
          console.log("Unknown action:", action, "- refetching monitor query");
          queryClient.refetchQueries({ queryKey });
          return;
        }

        if (newState !== null) {
          // Optimistički uskladi I state I sync (bez toga bi timer na monitoru na pauzi
          // nastavio teći, a na nastavku ostao zamrznut - čita autoritativni `sync`).
          queryClient.setQueryData(
            queryKey,
            applyMonitorStateChange(currentMonitorData, [examInstanceId], newState)
          );

          // Prisilni autoritativni refetch: donosi precizni (deterministički) endsAt.
          queryClient.refetchQueries({ queryKey });
          console.log("Applied consistent state+sync and refetched:", newState);
        }
      }
    };
    
    console.log("Registering SignalR events handler...");
    const cleanUp = events(handleMessage);
    console.log("SignalR handler registered, cleanup function:", typeof cleanUp);

    return () => {
      console.log("Cleaning up SignalR listener for exam:", exam?.id);
      cleanUp();
    };
  }, [events, exam?.id, queryClient]);

  return (
    <>
      <Typography.Title level={4}>{exam?.exam.title}</Typography.Title>
      <Space.Compact direction="vertical" style={{ width: "100%" }}>
        <Row gutter={16}>
          <Col span={8} style={{ display: "flex" }}>
            <div
              style={{
                width: 3,
                backgroundColor: "#F1B80E",
                borderRadius: 100,
                marginRight: 12,
              }}
            />
            <Space direction="vertical" style={{ display: "flex" }} size={0}>
              <Typography.Paragraph style={{ margin: 0 }}>
                <Typography.Text strong>
                  {t("pages.activity.examDate")}:{" "}
                </Typography.Text>
                <ExamTermTime date={exam?.date} />
              </Typography.Paragraph>
              <Typography.Paragraph style={{ margin: 0 }}>
                <Typography.Text strong>
                  {t("pages.activity.examDuration")}:{" "}
                </Typography.Text>
                <Typography.Text>
                  {setTimeWithSeconds(exam?.time).format("H[h] mm[min]")}
                </Typography.Text>
              </Typography.Paragraph>
            </Space>
          </Col>
          <Col span={8} style={{ display: "flex" }}>
            <div
              style={{
                width: 3,
                backgroundColor: "#F1B80E",
                borderRadius: 100,
                marginRight: 12,
              }}
            />
            <Space direction="vertical" style={{ display: "flex" }} size={0}>
              <Typography.Paragraph style={{ margin: 0 }}>
                <Typography.Text strong>
                  {t("pages.activity.contact")}:{" "}
                </Typography.Text>
                <Typography.Text>{exam?.contact && `${exam?.contact?.firstName} ${exam?.contact?.lastName}`}</Typography.Text>
              </Typography.Paragraph>
              <Typography.Paragraph style={{ margin: 0 }}>
                <Typography.Text strong>
                  {t("pages.activity.place")}:{" "}
                </Typography.Text>
                <Typography.Text>{exam?.examRoom.location}</Typography.Text>
              </Typography.Paragraph>
            </Space>
          </Col>
          <Col span={8} style={{ display: "flex" }}>
            <div
              style={{
                width: 3,
                backgroundColor: "#F1B80E",
                borderRadius: 100,
                marginRight: 12,
              }}
            />
            <Space direction="vertical" style={{ display: "flex" }} size={0}>
              <Typography.Paragraph style={{ margin: 0 }}>
                <Typography.Text strong>
                  {t("pages.activity.monitoring")}:{" "}
                </Typography.Text>
                <Typography.Text> {exam?.monitoring ? t("yes") : t("no")} </Typography.Text>
              </Typography.Paragraph>
              {exam?.videoProctoringEnabled && exam?.id && (
                <Typography.Paragraph style={{ margin: 0 }}>
                  <Typography.Text strong>
                    Video Proctoring:{" "}
                  </Typography.Text>
                  <Typography.Link href={`/${getKeycloakRealm}/proctoring/${exam.id}`} target="_blank">
                    {t("pages.activity.openMeeting") || "Open Meeting"}
                  </Typography.Link>
                </Typography.Paragraph>
              )}
              <Typography.Paragraph style={{ margin: 0 }}>
                <Typography.Text strong>
                  {t("pages.activity.status")}:{" "}
                </Typography.Text>
                {getTextByExamPeriodStatus(exam?.state, t)}
              </Typography.Paragraph>
            </Space>
          </Col>
        </Row>
      </Space.Compact>
      {exam?.state !== ExamPeriodState.Completed && (
        <Space
          style={{
            display: "flex",
            justifyContent: "flex-end",
            margin: "8px 0",
          }}
        >
          {!checkIfRegisterAll() && (
            <Button
              type="dashed"
              onClick={() => {
                  newMessage("registerAll-" + exam?.id);
                  handleClickAll(ExamPeriodMonitorState.ParticipantConfirmed)
                }
              }
              loading={editExamInstance.isPending && buttonClicked === 2}
              icon={<Flag />}
            >
              {t("pages.examPeriods.registerAll")}
            </Button>
          )}
          <Button
            type="primary"
            onClick={() => {
              handleClickAll(ExamPeriodMonitorState.Paused);
              newMessage("pauseAll-"+exam?.id);
            }}
            loading={editExamInstance.isPending && buttonClicked === 5}
          >
            {t("pages.examPeriods.pauseAll")}
          </Button>
          <Button
            onClick={() => {
              handleClickAll(ExamPeriodMonitorState.InProgress);
              newMessage("continueAll-"+exam?.id);
            }}
            loading={editExamInstance.isPending && buttonClicked === 4}
          >
            {t("pages.examPeriods.continueAll")}
          </Button>
          <Button
            danger
            type="primary"
            loading={completeExamPeriod.isPending}
            onClick={() => {
              completeExamPeriod.mutate({ id: exam?.id });
              newMessage("finishAll-"+exam?.id);
              handleClickAll(ExamPeriodMonitorState.Completed);
            }}
          >
            {t("pages.examPeriods.finish")}
          </Button>
        </Space>
      )}
      <Table
        style={{ marginTop: 15 }}
        rowKey={(record) => record.id}
        size="small"
        dataSource={monitorData}
        loading={isLoading}
        columns={examPeriodActivityTable(exam)}
        expandable={{
          rowExpandable: (record) => record.userLogins.length > 0 || record.proctoringEvents.length > 0 || (record.monitorActionLogs?.length ?? 0) > 0 || canSeeAnswerAudit,
          expandedRowRender: (record) => (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Typography.Text strong>{t("pages.activity.summary")}</Typography.Text>
                <Space wrap style={{ display: "flex", marginTop: 8 }}>
                  <Tag color={record.requiresSafeExamBrowser ? (record.hasSafeExamBrowserViolation ? "error" : "success") : "default"}>
                    {record.requiresSafeExamBrowser
                      ? record.hasSafeExamBrowserViolation
                        ? t("pages.activity.safeExamBrowserMissing")
                        : t("pages.activity.safeExamBrowserDetected")
                      : t("pages.activity.safeExamBrowserNotRequired")}
                  </Tag>
                  <Tag color={record.hasMultipleSessions ? "error" : "default"}>
                    {t("pages.activity.sessionCount", { count: record.sessionCount })}
                  </Tag>
                  {record.lastExitAt && (
                    <Tag color="warning">
                      {t("pages.activity.lastExitAt")}: {dayjs(record.lastExitAt).format("DD.MM.YYYY HH:mm:ss")}
                    </Tag>
                  )}
                  {record.startedAt && (
                    <Tag color="processing">
                      {t("pages.activity.startedAt")}: {dayjs(record.startedAt).format("DD.MM.YYYY HH:mm:ss")}
                    </Tag>
                  )}
                </Space>
              </div>

              <div>
                <Typography.Text strong>{t("pages.activity.sessionLogins")}</Typography.Text>
                <Space direction="vertical" style={{ display: "flex", marginTop: 8 }} size={4}>
                  {record.userLogins.length === 0 && (
                    <Typography.Text type="secondary">{t("pages.activity.noLoginSessions")}</Typography.Text>
                  )}
                  {record.userLogins.map((login, index) => (
                    <div key={`${record.id}-login-${index}`} style={{ borderLeft: "3px solid #d9d9d9", paddingLeft: 12 }}>
                      <Typography.Text>
                        {login.loggedInAt ? dayjs(login.loggedInAt).format("DD.MM.YYYY HH:mm:ss") : "-"}
                      </Typography.Text>
                      <Typography.Paragraph style={{ marginBottom: 0 }} type="secondary">
                        IP: {login.ipAddress || "-"} | Proxy: {login.proxyIPAdress || "-"}
                      </Typography.Paragraph>
                      <Typography.Paragraph style={{ marginBottom: 0 }} type="secondary">
                        {login.userAgent || "-"}
                      </Typography.Paragraph>
                    </div>
                  ))}
                </Space>
              </div>

              <div>
                <Typography.Text strong>{t("pages.activity.adminActions")}</Typography.Text>
                <Space direction="vertical" style={{ display: "flex", marginTop: 8 }} size={4}>
                  {(record.monitorActionLogs?.length ?? 0) === 0 && (
                    <Typography.Text type="secondary">{t("pages.activity.noAdminActions")}</Typography.Text>
                  )}
                  {record.monitorActionLogs?.map((action) => (
                    <div key={action.id} style={{ borderLeft: "3px solid #1677ff", paddingLeft: 12 }}>
                      <Space wrap>
                        <Typography.Text strong>
                          {getMonitorStateLabel(action.oldState)} - {getMonitorStateLabel(action.newState)}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          {action.dateCreated ? dayjs(action.dateCreated).format("DD.MM.YYYY HH:mm:ss") : "-"}
                        </Typography.Text>
                        {(action.actorName || action.actorEmail) && (
                          <Tag>{action.actorName || action.actorEmail}</Tag>
                        )}
                      </Space>
                      {action.actorName && action.actorEmail && (
                        <Typography.Paragraph style={{ marginBottom: 0 }} type="secondary">
                          {action.actorEmail}
                        </Typography.Paragraph>
                      )}
                    </div>
                  ))}
                </Space>
              </div>

              <div>
                <Typography.Text strong>{t("pages.activity.proctoringTimeline")}</Typography.Text>
                <Space direction="vertical" style={{ display: "flex", marginTop: 8 }} size={4}>
                  {record.proctoringEvents.length === 0 && (
                    <Typography.Text type="secondary">{t("pages.activity.noProctoringEvents")}</Typography.Text>
                  )}
                  {record.proctoringEvents.map((event) => (
                    <div key={event.id} style={{ borderLeft: "3px solid #F1B80E", paddingLeft: 12 }}>
                      <Space wrap>
                        <Typography.Text strong>{getProctoringEventLabel(event.eventType)}</Typography.Text>
                        <Typography.Text type="secondary">
                          {event.dateCreated ? dayjs(event.dateCreated).format("DD.MM.YYYY HH:mm:ss") : "-"}
                        </Typography.Text>
                        {event.safeExamBrowserDetected && (
                          <Tag color="success">SEB</Tag>
                        )}
                        {event.sessionId && (
                          <Tag>{event.sessionId}</Tag>
                        )}
                      </Space>
                      {(event.details || event.ipAddress || event.userAgent) && (
                        <Typography.Paragraph style={{ marginBottom: 0 }} type="secondary">
                          {[event.details, event.ipAddress && `IP: ${event.ipAddress}`, event.userAgent].filter(Boolean).join(" | ")}
                        </Typography.Paragraph>
                      )}
                    </div>
                  ))}
                </Space>
              </div>

              {canSeeAnswerAudit && (
                <AnswerAuditPanel examInstanceId={record.id} canView={canSeeAnswerAudit} />
              )}
            </Space>
          ),
        }}
      />
    </>
  );
};
